import React, { useState, useEffect } from 'react';
import { Modal } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaXmark } from 'react-icons/fa6';
import useAssignedLibrary from 'utils/customHooks/useAssignedLibrary';
import { addItemsByEan, getCart, addItemCart } from 'api/ecommerce';
import { useCartStore, useCompanyStore, useUserStore } from 'stores';

const QuickOrderModal = ({ show, onClose }) => {
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { assignedLibrary } = useAssignedLibrary();
  const navigate = useNavigate();
  const { t } = useTranslation(['home']);
  const { addItem, setGiftCard } = useCartStore();
  const { shipping_cost, shipping_cost_islands, min_purchase_for_free_shipping } = useCompanyStore();
  const { isLogged, shippingAddress } = useUserStore();

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        setError(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  useEffect(() => {
    if (!show) {
      setText('');
      setError(null);
      setLoading(false);
    }
  }, [show]);

  const processEans = (text) => {
    return text
      .split('\n')
      .map(line => line.trim())
      .filter(line => line.length > 0);
  };

  const handleAdd = async () => {
    const eans = processEans(text);
    
    if (eans.length === 0) {
      setError(t('pleaseEnterAtLeastOneEan'));
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const payload = { eans };
      const response = await addItemsByEan(payload);

      if (response.status === 200 || response.status === 201) {
        const itemsToAdd = response.data?.items || [];
        const noExist = response.data?.no_exist || [];
        const noStock = response.data?.no_stock || [];
        if (
          Array.isArray(itemsToAdd) && itemsToAdd.length === 0 &&
          (Array.isArray(noExist) && noExist.length > 0 ||
           Array.isArray(noStock) && noStock.length > 0)
        ) {
          const parts = [];
          if (Array.isArray(noExist) && noExist.length > 0) {
            const codesList = noExist.join(', ');
            const msgNoExist =
              noExist.length === 1
                ? t('eanDoesNotExist', { codes: codesList })
                : t('eansDoNotExist', { codes: codesList });
            parts.push(msgNoExist);
          }
          if (Array.isArray(noStock) && noStock.length > 0) {
            const codesList = noStock.join(', ');
            const msgNoStock =
              noStock.length === 1
                ? t('eanNoStock', { codes: codesList })
                : t('eansNoStock', { codes: codesList });
            parts.push(msgNoStock);
          }
          setError(parts.join('\n'));
          setLoading(false);
          return;
        }

        if (Array.isArray(itemsToAdd) && itemsToAdd.length > 0) {
          for (const it of itemsToAdd) {
            await addItemCart({ item: it.item, quantity: it.quantity || 1 });
          }
        }
        if (isLogged) {
          const cart = await getCart();
          if (cart.status === 200 && cart.data?.items) {
            const province =
              cart?.data?.shipping_address?.province?.label ||
              cart?.data?.shipping_address?.province ||
              shippingAddress?.province?.label ||
              shippingAddress?.province ||
              '';

            for (const item of cart.data.items) {
              addItem({
                product: item.unit_price != null
                  ? { ...item.producto, unit_price: item.unit_price, price: item.unit_price }
                  : item.producto,
                province,
                shippingCost: shipping_cost,
                shippingCostIslands: shipping_cost_islands,
                minPurchaseForFreeShipping: min_purchase_for_free_shipping,
                quantity: item.quantity || 1,
                setQuantity: true,
                bookCondition: item.book_condition || item.producto?.book_condition,
              });
            }

            if (
              cart.data.giftcard_purchased &&
              Object.keys(cart.data.giftcard_purchased).length > 0 &&
              cart.data.giftcard_purchased.amount > 0
            ) {
              setGiftCard({
                id: cart.data.giftcard_purchased.id,
                code: cart.data.giftcard_purchased.code,
                amount: cart.data.giftcard_purchased.amount,
                discount_amount: cart.data.giftcard_purchased.amount,
              });
            }
          }
        }
        onClose();
        setText('');
        navigate(`/${assignedLibrary}/carrito`, { state: { noExist, noStock } });
      } else {
        setError(t('reviewCodesTryAgain'));
        setLoading(false);
      }
    } catch (err) {
      setError(t('reviewCodesTryAgain'));
      setLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={onClose} centered className="search-options-modal">
      <Modal.Header className="search-modal-header">
        <Modal.Title className="search-modal-title">{t('quickOrder')}</Modal.Title>
        <button 
          type="button" 
          className="search-modal-close" 
          onClick={onClose}
          aria-label="Cerrar"
        >
          <FaXmark size={20} />
        </button>
      </Modal.Header>
      <Modal.Body className="search-modal-body">
        <p>{t('enterOneOrMoreEanCodes')}</p>
        <textarea
          className="form-control quick-order-textarea"
          value={text}
          onChange={(e) => {
            const filteredValue = e.target.value.replace(/[^\d\n]/g, '');
            setText(filteredValue);
            setError(null);
          }}
          placeholder={"978841262786\n978812112232\n941212241263\n932388412632\n978843232333"}
          disabled={loading}
        />
        {error && (
          <div className="alert alert-danger mt-2" role="alert">
            {error}
          </div>
        )}
        <div className="search-modal-advanced">
          <button
            className="search-advanced-link"
            onClick={handleAdd}
            disabled={loading || text.trim().length === 0}
            style={{ 
              cursor: (loading || text.trim().length === 0) ? 'not-allowed' : 'pointer',
              opacity: (loading || text.trim().length === 0) ? 0.6 : 1
            }}
          >
            {loading ? t('adding') : t('anadirCesta')}
          </button>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default QuickOrderModal;
